import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import Icon from "../../../../Icon/Icon";
import { useTranslation } from "react-i18next";
import { OutlineArrowDown, OutlineArrowUp2 } from "../../../../Icon";

/**
 * SortMenuInterviews Component
 *
 * Provides a UI menu to sort a list of interview objects by:
 * - Scheduled date
 * - Status
 * - Interviewer label/name
 *
 * Sorting is non-mutating. When a sort operation produces a different order,
 * the component invokes `onSorted(sortedList)`.
 *
 * If the list contains 0–1 items, the component renders nothing.
 */

export const SortMenuInterviews = ({
  interviews = [],
  onSorted,
  getUserLabel = (user) => {
    if (!user) return "";
    if (typeof user === "string") return user;
    return user?.id ?? "";
  },
}) => {
  const [t] = useTranslation("investigation");

  const [anchorEl, setAnchorEl] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [selectedIcon, setSelectedIcon] = useState("arrow_3");

  // Opens/closes the sorting menu //
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Returns a status ordering map depending on direction //
  const statusOrder = useCallback(
    (dir) =>
      dir === "asc"
        ? { pending: 0, process: 1, finish: 2 }
        : { finish: 0, process: 1, pending: 2 },
    []
  );

  // Locale-aware string comparator //
  const collator = useRef(
    new Intl.Collator(undefined, { sensitivity: "base" })
  );

  // Safely parses a date string. Returns NaN for invalid values //
  const parseTimeSafe = useCallback((value) => {
    if (!value) return NaN;
    const t = Date.parse(value);
    return isNaN(t) ? NaN : t;
  }, []);

  // ---- COMPARATORS ---- //
  // Comparator for scheduled date sorting.
  // Handles missing dates, invalid dates, and direction.
  const cmpScheduled = useCallback(
    (dir) => (a, b) => {
      const aHas = !!a?.scheduled;
      const bHas = !!b?.scheduled;

      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      if (!aHas && !bHas) return 0;

      const aTime = parseTimeSafe(a.scheduled);
      const bTime = parseTimeSafe(b.scheduled);

      if (isNaN(aTime) && isNaN(bTime)) return 0;
      if (isNaN(aTime)) return 1;
      if (isNaN(bTime)) return -1;

      return dir === "desc" ? bTime - aTime : aTime - bTime;
    },
    [parseTimeSafe]
  );

  // Comparator for interview status using directional ranking.
  const cmpStatus = useCallback(
    (dir) => (a, b) => {
      const order = statusOrder(dir);
      const ra = typeof a?.status === "string" ? order[a.status] ?? 3 : 3;
      const rb = typeof b?.status === "string" ? order[b.status] ?? 3 : 3;
      return ra - rb;
    },
    [statusOrder]
  );

  // Comparator for interviewer labels using locale-aware string comparison //
  const cmpInterviewer = useCallback(
    (dir) => (a, b) => {
      let la = getUserLabel(a?.interviewer) ?? a?.interviewer?.id ?? "";
      let lb = getUserLabel(b?.interviewer) ?? b?.interviewer?.id ?? "";

      la = String(la).toLowerCase();
      lb = String(lb).toLowerCase();

      const cmp = collator.current.compare(la, lb);
      return dir === "desc" ? -cmp : cmp;
    },
    [getUserLabel]
  );

  // Map of comparator factories by criterion (keeps adding new criteria easy) //
  const comparatorByCriterion = useMemo(
    () => ({
      scheduled: cmpScheduled,
      status: cmpStatus,
      interviewer: cmpInterviewer,
    }),
    [cmpScheduled, cmpStatus, cmpInterviewer]
  );

  // ---- SORTING UTILITIES ---- //
  // Returns a new sorted copy of the interview list
  const getSortedList = useCallback(
    (list, criterion, dir) => {
      if (!Array.isArray(list)) return [];
      const copy = [...list];

      if (!criterion) return copy;

      const cmpFactory = comparatorByCriterion[criterion];
      if (typeof cmpFactory === "function") {
        copy.sort(cmpFactory(dir));
      }

      return copy;
    },
    [comparatorByCriterion]
  );

  /**
   * Compares two arrays element-by-element using:
   * - the `id` property if available
   * - JSON stringification otherwise
   * Prevents unnecessary onSorted calls.**/
  const arraysEqualById = useCallback((a = [], b = []) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const idA = a[i]?.id ?? JSON.stringify(a[i]);
      const idB = b[i]?.id ?? JSON.stringify(b[i]);
      if (idA !== idB) return false;
    }
    return true;
  }, []);

  // Applies sorting and triggers `onSorted` only if the result differs. //
  const applySort = useCallback(
    (criterion, dir) => {
      if (!Array.isArray(interviews)) return;

      const sorted = getSortedList(interviews, criterion, dir);

      if (arraysEqualById(interviews, sorted)) return;

      onSorted?.(sorted);
    },
    [interviews, getSortedList, onSorted, arraysEqualById]
  );

  // ---- UI SELECTION HANDLER ---- //
  const iconMap = useMemo(
    () => ({
      scheduled: "calendar2",
      status: "activity",
      interviewer: "personal_id",
    }),
    []
  );

  // Menu options to avoid repeating JSX for each MenuItem //
  const menuOptions = useMemo(
    () => [
      {
        key: "scheduled",
        label: t("Interview.scheduled_date"),
        icon: "calendar2",
      },
      { key: "status", label: t("Interview.status"), icon: "activity" },
      {
        key: "interviewer",
        label: t("Interview.interviewer"),
        icon: "personal_id",
      },
    ],
    []
  );

  // Handles selection of sorting criteria in the menu //
  // Toggles direction if selecting the same criterion twice //
  const onSelectCriterion = (criterion) => {
    let newDir = "asc";

    if (sortBy === criterion) {
      newDir = sortDir === "asc" ? "desc" : "asc";
    }

    setSortBy(criterion);
    setSortDir(newDir);
    setSelectedIcon(iconMap[criterion] ?? "arrow_3");

    const sorted = getSortedList(interviews, criterion, newDir);
    if (!arraysEqualById(interviews, sorted)) {
      onSorted?.(sorted);
    }

    handleClose();
  };

  // Icon arrow for selected button //
  const currentArrow =
    selectedIcon === "arrow_3" ? (
      ""
    ) : sortDir === "desc" ? (
      <OutlineArrowDown size={17} color={"grey"} />
    ) : (
      <OutlineArrowUp2 size={17} color={"grey"} />
    );

  // Returns directional arrow only for the active criterion //
  const arrowFor = (criterion) => {
    if (sortBy !== criterion) return "";
    return sortDir === "desc" ? (
      <OutlineArrowDown size={19} color={"grey"} />
    ) : (
      <OutlineArrowUp2 size={19} color={"grey"} />
    );
  };

  // Re-applies sorting when parent updates `interviews`.
  // Only triggers `onSorted` if actual order changes.
  useEffect(() => {
    if (!sortBy) return;
    applySort(sortBy, sortDir);
  }, [interviews, sortBy, sortDir, applySort]);

  if (!Array.isArray(interviews) || interviews.length <= 2) return null;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <EGIconButton
          iconName={selectedIcon}
          iconSize={20}
          onClick={handleOpen}
          title={t("Interview.change_order")}
          badgeContent={currentArrow}
          badgeVariant="standard"
          cartBadgeColor="transparent"
        />
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {menuOptions.map(({ key, label, icon }) => (
          <MenuItem
            key={key}
            onClick={() => onSelectCriterion(key)}
            sx={{ m: 0.25, borderRadius: 4 }}
          >
            <ListItemIcon>
              <Icon name={icon} color={"gray"} />
            </ListItemIcon>
            <ListItemText primary={label} />
            <Typography variant="body1" fontSize={12} color="secondary">
              {arrowFor(key)}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
